import type { ReactNode } from "react";
import {
  DemographicRow,
  DemographicSeparator,
  EcgReportIcon,
} from "./icons/patientDetailIcons";
import type { Patient, Report } from "../types";
import {
  getFamilyHeartHistoryDisplay,
  getHealthConditionsDisplay,
  getLifestyleDisplay,
  getMedicationsDisplay,
  getQuestionnaireList,
} from "../utils/medicalHistoryQuestionnaire";
import { InlineLoading } from "./loading/LoadingIndicators";

const BLOOD_REPORT_TYPES = ["crp", "cbc", "lipid_profile", "trop_i"] as const;

function formatReportDate(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getReportDate(report: Report): Date {
  return new Date(report.createdAt || report.uploadedAt || Date.now());
}

function getLatestReport(
  reports: Record<string, Report[]> | null,
  reportType: string
): Report | null {
  if (!reports?.[reportType]?.length) return null;
  const sorted = [...reports[reportType]].sort(
    (a, b) => getReportDate(b).getTime() - getReportDate(a).getTime()
  );
  return sorted[0] ?? null;
}

function getReportData(
  report: Report,
  reportAnalysis: Record<string, unknown>
): Record<string, unknown> | null {
  if (report.data && typeof report.data === "object") {
    return report.data as Record<string, unknown>;
  }
  const key = `${report.reportType}_${report._id}`;
  const fromAnalysis = reportAnalysis[key];
  if (fromAnalysis && typeof fromAnalysis === "object") {
    return fromAnalysis as Record<string, unknown>;
  }
  return null;
}

function formatCrpSegment(data: Record<string, unknown>): string | null {
  const crp_value = data.crp_value;
  const unit = data.unit;
  if (crp_value == null || crp_value === "") return null;
  const unitStr = unit ? ` ${unit}` : " mg/dL";
  return `CRP: ${crp_value}${unitStr}`;
}

function formatCbcSegment(data: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if (data.hemoglobin_g_dl != null)
    parts.push(`Hemoglobin: ${data.hemoglobin_g_dl} g/dL`);
  if (data.wbc_per_ul != null) parts.push(`WBC: ${data.wbc_per_ul}`);
  if (data.platelets_per_ul != null)
    parts.push(`Platelets: ${data.platelets_per_ul}`);
  return parts.length ? parts.join(", ") : null;
}

function formatLipidSegment(data: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if (data.total_cholesterol != null)
    parts.push(`Total cholesterol: ${data.total_cholesterol}`);
  if (data.ldl_cholesterol != null)
    parts.push(`LDL: ${data.ldl_cholesterol}`);
  if (data.hdl_cholesterol != null)
    parts.push(`HDL: ${data.hdl_cholesterol}`);
  return parts.length ? parts.join(", ") : null;
}

function formatTropSegment(data: Record<string, unknown>): string | null {
  const val = data.troponin_i_value;
  const unit = data.unit;
  if (val == null || val === "") return null;
  const unitStr = unit ? ` ${unit}` : "";
  return `Troponin I: ${val}${unitStr}`;
}

function formatBloodSegment(
  reportType: string,
  data: Record<string, unknown>
): string | null {
  switch (reportType) {
    case "crp":
      return formatCrpSegment(data);
    case "cbc":
      return formatCbcSegment(data);
    case "lipid_profile":
      return formatLipidSegment(data);
    case "trop_i":
      return formatTropSegment(data);
    default:
      return null;
  }
}

function EmptyValue() {
  return <span className="text-guardian-header">-</span>;
}

function HistoryField({
  label,
  children,
  valueClassName = "text-sm",
}: {
  label: string;
  children: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="text-sm font-medium text-guardian-body">{label}</div>
      <div className={valueClassName}>{children}</div>
    </div>
  );
}

function HistoryRow({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2.5 border-b border-guardian-10 pb-2.5 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  );
}

export interface MedicalHistoryTabProps {
  patient: Patient;
  reports: Record<string, Report[]> | null;
  reportAnalysis: Record<string, unknown>;
  reportsLoading: boolean;
  onReportClick: (reportType: string, fileName: string) => void;
}

export default function MedicalHistoryTab({
  patient,
  reports,
  reportAnalysis,
  reportsLoading,
  onReportClick,
}: MedicalHistoryTabProps) {
  const symptoms = getQuestionnaireList(patient, "symptoms");
  const healthConditions = getHealthConditionsDisplay(patient);
  const medications = getMedicationsDisplay(patient);
  const lifestyle = getLifestyleDisplay(patient);
  const familyHeartHistory = getFamilyHeartHistoryDisplay(patient);

  const latestEcg = getLatestReport(reports, "ecg");
  const ecgData = latestEcg
    ? getReportData(latestEcg, reportAnalysis)
    : null;
  const ecgImpression =
    (ecgData?.ecg_impression as string) || "";

  const bloodSegments: { date: Date; text: string }[] = [];
  let newestBloodDate: Date | null = null;

  BLOOD_REPORT_TYPES.forEach((type) => {
    const report = getLatestReport(reports, type);
    if (!report) return;
    const data = getReportData(report, reportAnalysis);
    if (!data) return;
    const text = formatBloodSegment(type, data);
    if (!text) return;
    const date = getReportDate(report);
    bloodSegments.push({ date, text });
    if (!newestBloodDate || date.getTime() > newestBloodDate.getTime()) {
      newestBloodDate = date;
    }
  });

  return (
    <div className="flex flex-col gap-3">
      <HistoryRow>
        <HistoryField label="Symptoms" valueClassName="text-sm text-vital-high">
          {symptoms || <EmptyValue />}
        </HistoryField>
        <HistoryField
          label="Health Conditions"
          valueClassName="text-sm text-vital-high"
        >
          {healthConditions || <EmptyValue />}
        </HistoryField>
        <HistoryField label="Medications" valueClassName="text-sm text-guardian-80">
          {medications || <EmptyValue />}
        </HistoryField>
      </HistoryRow>

      <HistoryRow>
        <HistoryField label="Lifestyle" valueClassName="text-sm text-vital-high">
          {lifestyle || <EmptyValue />}
        </HistoryField>
        <HistoryField
          label="Family Heart History"
          valueClassName="text-sm text-guardian-header"
        >
          {familyHeartHistory || <EmptyValue />}
        </HistoryField>
        <HistoryField label="ECG" valueClassName="text-sm">
          {reportsLoading ? (
            <InlineLoading message="Loading..." />
          ) : latestEcg && ecgImpression ? (
            <DemographicRow>
              <span className="text-sm font-normal text-guardian-header">
                {formatReportDate(getReportDate(latestEcg))}
              </span>
              <DemographicSeparator />
              <span className="inline-flex shrink-0 items-center gap-2">
                <EcgReportIcon />
                <button
                  type="button"
                  className="text-sm font-normal text-blue-600 underline hover:text-blue-700"
                  onClick={() => onReportClick("ecg", latestEcg.fileName)}
                >
                  {ecgImpression}
                </button>
              </span>
            </DemographicRow>
          ) : latestEcg ? (
            <DemographicRow>
              <span className="text-sm font-normal text-guardian-header">
                {formatReportDate(getReportDate(latestEcg))}
              </span>
              <DemographicSeparator />
              <span className="inline-flex shrink-0 items-center gap-2">
                <EcgReportIcon />
                <button
                  type="button"
                  className="text-sm font-normal text-blue-600 underline hover:text-blue-700"
                  onClick={() => onReportClick("ecg", latestEcg.fileName)}
                >
                  View report
                </button>
              </span>
            </DemographicRow>
          ) : (
            <EmptyValue />
          )}
        </HistoryField>
      </HistoryRow>

      <div className="pb-2.5">
        <HistoryField
          label="Blood Investigation"
          valueClassName="text-sm text-guardian-80"
        >
          {reportsLoading ? (
            <InlineLoading message="Loading..." />
          ) : bloodSegments.length > 0 && newestBloodDate ? (
            <DemographicRow>
              <span className="text-sm font-normal text-guardian-header">
                {formatReportDate(newestBloodDate)}
              </span>
              {bloodSegments.map((seg, index) => (
                <span key={`${seg.text}-${index}`} className="contents">
                  <DemographicSeparator />
                  <span className="text-sm font-normal text-guardian-80">
                    {seg.text}
                  </span>
                </span>
              ))}
            </DemographicRow>
          ) : (
            <EmptyValue />
          )}
        </HistoryField>
      </div>
    </div>
  );
}
